import { useMemo, useEffect } from 'react';

import { IWidgetCustomization, WidgetFontType } from 'widget/entities/config';
import {
  FontMap,
  WidgetFontName,
  ensureGoogleFont
} from 'widget/entities/fonts';
import {
  Theme,
  darkPalette,
  LIGHT_TEXT_PRIMARY,
  DARK_TEXT_PRIMARY
} from 'widget/entities/themes';
import { WidgetEvents } from 'widget/types/events';
import { getPreferredContrastColor } from 'widget/utils/color';
import { postMessageToParent } from 'widget/utils/postMessageToParent';

import { useResolvedTheme } from './useResolvedTheme';

type UseWidgetCustomizationReturn = {
  style: React.CSSProperties;
};

export const useWidgetCustomization = (
  customizationConfig?: IWidgetCustomization
): UseWidgetCustomizationReturn => {
  const font = customizationConfig?.font;
  const resolvedTheme = useResolvedTheme(customizationConfig?.theme);

  const style = useMemo<React.CSSProperties>(() => {
    if (!customizationConfig) {
      return {};
    }

    const customStyle: Record<string, string> = {};

    if (resolvedTheme === Theme.dark) {
      Object.assign(customStyle, darkPalette);
    }

    if (customizationConfig.primaryColor) {
      customStyle['--color-main'] = customizationConfig.primaryColor;

      customStyle['--color-accent-text'] = getPreferredContrastColor(
        customizationConfig.primaryColor,
        DARK_TEXT_PRIMARY,
        LIGHT_TEXT_PRIMARY
      );
    }

    if (customizationConfig.background?.url) {
      customStyle.backgroundImage = `url(${customizationConfig.background.url})`;
      customStyle.backgroundSize = 'cover';
      customStyle.backgroundPosition = 'center';
    }

    const defaultFallback =
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

    if (font?.type === WidgetFontType.preset) {
      const fontInfo = FontMap[font.name as WidgetFontName];

      if (fontInfo) {
        customStyle.fontFamily = `'${fontInfo.family}', ${defaultFallback}`;
      } else {
        customStyle.fontFamily = `'Roboto', ${defaultFallback}`;
      }
    } else if (font?.type === WidgetFontType.custom) {
      customStyle.fontFamily = `'CustomWidgetFont', ${defaultFallback}`;
    } else {
      customStyle.fontFamily = `'Roboto', ${defaultFallback}`;
    }

    return customStyle as React.CSSProperties;
  }, [customizationConfig, resolvedTheme, font]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="color-scheme"]');

    if (meta) {
      meta.setAttribute('content', resolvedTheme);
    }

    postMessageToParent(WidgetEvents.THEME_RESOLVED, {
      colorScheme: resolvedTheme
    });
  }, [resolvedTheme]);

  // Load preset Google Font
  useEffect(() => {
    if (font?.type !== WidgetFontType.preset) {
      return;
    }

    ensureGoogleFont(font.name as WidgetFontName);
  }, [font]);

  // Load custom uploaded font via @font-face
  useEffect(() => {
    if (font?.type !== WidgetFontType.custom) {
      return;
    }

    const styleId = 'widget-custom-font';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      @font-face {
        font-family: 'CustomWidgetFont';
        src: url('${font.url}') format('woff2');
        font-weight: normal;
        font-style: normal;
      }
    `;

    return () => {
      const element = document.getElementById(styleId);
      if (element) {
        element.remove();
      }
    };
  }, [font]);

  return { style };
};
